import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import sessionService from '../../services/sessionAPI';

const initialState = {
  user: null,
  token: null,
  roles: [],
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const data = await sessionService.me();
      const user = data?.user ?? data?.data ?? null;
      return { user };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to load current user'
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      // Clear impersonation token before new login
      sessionStorage.removeItem('impersonation_token');
      
      const data = await sessionService.login(credentials);
      const user = data?.user ?? data?.data?.user ?? null;
      const token = data?.token ?? null;
      try {
        if (user?.email || user?.id) {
          await fetch('/api/activities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              action: 'login',
              page: '/login',
              email: user?.email || null,
              user_id: user?.id || null,
              meta: null,
            }),
          });
        }
      } catch (_) {}
      return { user, token };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Login failed'
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if we are in an impersonation session
      const impersonationToken = sessionStorage.getItem('impersonation_token');
      
      // Clear impersonation token
      sessionStorage.removeItem('impersonation_token');

      // If we were impersonating, do NOT call backend logout to preserve Admin session
      if (impersonationToken) {
        return true;
      }

      await sessionService.logout();
      try {
        const state = getState();
        const auth = state?.auth || {};
        const user = auth?.user || null;
        await fetch('/api/activities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            action: 'logout',
            page: '/logout',
            email: user?.email || null,
            user_id: user?.id || null,
            meta: null,
          }),
        });
      } catch (_) {}
      return true;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Logout failed'
      );
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const data = await sessionService.me();
      const user = data?.user ?? data?.data ?? null;
      return { user };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Token refresh failed'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      const payload = action.payload || {};
      const user = payload.user !== undefined ? payload.user : payload;
      state.user = user || null;
      state.token = payload.token || state.token || null;
      state.roles = user?.roles || [];
      state.isAuthenticated = !!user;
      state.isLoading = false;
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.roles = [];
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.roles = action.payload.user?.roles || [];
        state.isAuthenticated = !!action.payload.user;
        state.error = null;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.roles = [];
        state.isAuthenticated = false;
        state.error = action.payload || action.error?.message || 'Failed to load current user';
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token || null;
        state.roles = action.payload.user?.roles || [];
        state.isAuthenticated = !!action.payload.user;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error?.message || 'Login failed';
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.roles = [];
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        if (action.payload.user) {
          state.user = action.payload.user;
          state.roles = action.payload.user?.roles || state.roles;
          state.isAuthenticated = true;
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || 'Token refresh failed';
      });
  },
});

export const { setUser, clearAuth, clearError } = authSlice.actions;
export const selectAuth = (state) => state.auth;
export default authSlice.reducer;
